import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { AnalysesAccessCell } from "Components/Analyses/Listing/AnalysesAccessCell";
import { AnalysesEmptyState } from "Components/Analyses/Listing/AnalysesEmptyState";
import { AnalysesFamilyMembersCell } from "Components/Analyses/Listing/AnalysesFamilyMembersCell";
import { AnalysesTableActionsCell } from "Components/Analyses/Listing/AnalysesTableActionsCell";
import { AnalysisStatusChip } from "Components/Analyses/_Shared/AnalysisStatusChip";
import { Button } from "Components/_Shared/Buttons/Button";
import { NewAnalysisButton } from "Components/_Shared/Buttons/NewAnalysisButton";
import { BlDataTable } from "Components/_Shared/DataTable/BlDataTable";
import { ReactComponent as SearchIcon } from "Components/_Shared/Icons/Assets/search.svg";
import { type DateRange, DateRangePicker } from "Components/_Shared/Inputs/DatePickers/DateRangePicker";
import { TextInput } from "Components/_Shared/Inputs/TextInput";
import { AuthenticatedLayout } from "Components/_Shared/Layouts/AuthenticatedLayout";
import { TabFilter } from "Components/_Shared/TabFilter";
import { UserInitialsAvatar } from "Components/_Shared/UserInitialsAvatar";
import { useListAnalysesOverview } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  type AnalysesOverviewList,
  type AnalysisOverviewItem,
  AnalysisOverviewItemState,
  ListAnalysesOverviewScope,
  ListAnalysesOverviewSortBy,
  ListAnalysesOverviewSortOrder,
} from "Infrastructure/ApiZfp/Schema";
import { useFormatLocale } from "Infrastructure/Hooks/DateFns/useFormatLocale";
import useDebounce from "Infrastructure/Hooks/useDebounce";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { format } from "date-fns";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnalysesTableHeaderCell } from "../../Components/Analyses/Listing/AnalysesTableHeaderCell";

const SHARED_FILTER = ListAnalysesOverviewScope.shared;

type AnalysesListingFilter = AnalysisOverviewItemState | typeof SHARED_FILTER | null;

export const AnalysesListingPage: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { t } = useResource();
  const isAnalysisSharingEnabled = useFeatureFlag(FeatureFlag.AnalysisSharing);

  const [listingFilter, setListingFilter] = useState<AnalysesListingFilter>(null);
  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [sortOrder, setSortOrder] = useState<ListAnalysesOverviewSortOrder>(ListAnalysesOverviewSortOrder.desc);
  const [sortBy, setSortBy] = useState<ListAnalysesOverviewSortBy | undefined>(ListAnalysesOverviewSortBy.updated_at);
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [page, setPage] = useState(0);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const formatLocale = useFormatLocale();

  const { data, isLoading } = useListAnalysesOverview({
    state: listingFilter && listingFilter !== SHARED_FILTER ? listingFilter : undefined,
    scope: listingFilter === SHARED_FILTER ? SHARED_FILTER : undefined,
    dateFrom: dateRange?.startDate && format(dateRange.startDate, "yyyy-MM-dd"),
    dateTo: dateRange?.endDate && format(dateRange.endDate, "yyyy-MM-dd"),
    limit,
    page: page + 1,
    searchQuery: debouncedSearchValue,
    sortBy,
    sortOrder,
  });

  const handleFilterChange = (_: React.SyntheticEvent, filter: AnalysesListingFilter) => {
    setListingFilter(filter);
    setPage(0);
  };

  const handleColumnHeaderClick = (column: ListAnalysesOverviewSortBy) => {
    if (!sortBy) {
      setSortBy(column);
    } else {
      setSortBy(column === sortBy ? sortBy : column);
    }

    setSortOrder(
      column === sortBy && sortOrder === ListAnalysesOverviewSortOrder.asc
        ? ListAnalysesOverviewSortOrder.desc
        : ListAnalysesOverviewSortOrder.asc,
    );
  };

  const handleClearSearch = () => {
    setListingFilter(null);
    setSearchValue("");
    setPage(0);
    setDateRange({
      startDate: undefined,
      endDate: undefined,
    });
  };

  const renderPageContent = (data: AnalysesOverviewList) => {
    if (data.totalRecordsUnfiltered === 0) {
      return (
        <AnalysesEmptyState
          title={t(Resources.AnalysesListing.Table.EmptyState.NoData.Title)}
          subtitle={t(Resources.AnalysesListing.Table.EmptyState.NoData.Message)}
          cta={
            <Box>
              <NewAnalysisButton />
            </Box>
          }
          icon={<SearchIcon />}
        />
      );
    }

    if (data.data.length === 0) {
      return (
        <AnalysesEmptyState
          title={t(Resources.AnalysesListing.Table.EmptyState.NoResult.Title)}
          subtitle={
            debouncedSearchValue
              ? t(Resources.AnalysesListing.Table.EmptyState.NoResultWithQuery.Message, {
                  value: debouncedSearchValue,
                })
              : t(Resources.AnalysesListing.Table.EmptyState.NoResult.Message)
          }
          cta={
            <Box display="flex">
              <Box mr={Spacing.L}>
                <Button color="secondary" onClick={handleClearSearch}>
                  {t(Resources.AnalysesListing.Table.EmptyState.ClearSearch)}
                </Button>
              </Box>
              <NewAnalysisButton />
            </Box>
          }
          icon={<SearchIcon />}
        />
      );
    }

    const agentsByGuid = new Map(data.references.agents.map((agent) => [agent.guid, agent]));

    return (
      <BlDataTable
        initialPage={page}
        onRowClick={(data) => {
          navigate(AppRouting.AnalysisDetail.path(data.analysisId, data.versionId));
        }}
        totalCount={data.pagination.totalRecords}
        rowsPerPage={limit}
        onPageChange={(page, rows) => {
          setPage(page);
          setLimit(rows);
        }}
        getHeaderCell={(column) => (
          <AnalysesTableHeaderCell
            id={column.id}
            label={column.label}
            active={sortBy === column.sortBy}
            onHeaderClick={column.onHeaderClick}
          />
        )}
        rows={data.data.map((data: AnalysisOverviewItem) => ({
          data,
        }))}
        columns={[
          {
            id: "firstName",
            label: t(Resources.AnalysesListing.Table.HeaderCell.Client),
          },
          {
            id: "clientRelations",
            label: t(Resources.AnalysesListing.Table.HeaderCell.Family),
            maxWidth: 120,
          },
          {
            id: "name",
            label: t(Resources.AnalysesListing.Table.HeaderCell.Name),
            sortBy: ListAnalysesOverviewSortBy.name,
            onHeaderClick: () => handleColumnHeaderClick(ListAnalysesOverviewSortBy.name),
          },
          {
            id: "state",
            label: t(Resources.AnalysesListing.Table.HeaderCell.State),
            sortBy: ListAnalysesOverviewSortBy.state,
            onHeaderClick: () => handleColumnHeaderClick(ListAnalysesOverviewSortBy.state),
          },
          {
            id: "createdAt",
            label: t(Resources.AnalysesListing.Table.HeaderCell.DateCreated),
            maxWidth: 120,
            sortBy: ListAnalysesOverviewSortBy.created_at,
            onHeaderClick: () => handleColumnHeaderClick(ListAnalysesOverviewSortBy.created_at),
          },
          {
            id: "updatedAt",
            label: t(Resources.AnalysesListing.Table.HeaderCell.DateUpdated),
            maxWidth: 140,
            sortBy: ListAnalysesOverviewSortBy.updated_at,
            onHeaderClick: () => handleColumnHeaderClick(ListAnalysesOverviewSortBy.updated_at),
          },
          isAnalysisSharingEnabled
            ? { id: "sharing" as const, label: t(Resources.AnalysesListing.Table.HeaderCell.Access) }
            : { id: "action" as const, label: "", maxWidth: 100 },
        ]}
        rowEntityID="versionId"
        getCell={(id, data) => {
          switch (id) {
            case "firstName":
              return (
                <Box display="flex" alignItems="center">
                  <UserInitialsAvatar firstName={data.firstName} surname={data.surname} />
                  <Box ml={Spacing.L}>
                    <Typography variant="body2" fontWeight={500}>
                      {data.firstName} {data.surname}
                    </Typography>
                    <Typography variant="body2" color="secondary">
                      {data.email}
                    </Typography>
                  </Box>
                </Box>
              );
            case "clientRelations":
              return <AnalysesFamilyMembersCell clientRelations={data.clientRelations} />;
            case "name":
              return (
                <Box>
                  <Typography variant="body2">{data.name}</Typography>
                </Box>
              );
            case "state":
              return (
                <Box>
                  <AnalysisStatusChip state={data.state} />
                </Box>
              );
            case "createdAt":
              return (
                <Box>
                  <Typography variant="body2">{formatLocale(new Date(data.createdAt))}</Typography>
                </Box>
              );
            case "updatedAt":
              return (
                <Box>
                  <Typography variant="body2"> {formatLocale(new Date(data.updatedAt))}</Typography>
                </Box>
              );
            case "sharing":
              return <AnalysesAccessCell sharing={data.sharing} agentsByGuid={agentsByGuid} />;
            case "action":
              return <AnalysesTableActionsCell analysisId={data.analysisId} versionId={data.versionId} state={data.state} />;
            default:
              return <Box />;
          }
        }}
      />
    );
  };

  return (
    <AuthenticatedLayout title={t(Resources.AnalysesListing.Title)}>
      <Stack spacing={Spacing.XXXL}>
        <Stack spacing={Spacing.S}>
          <Typography variant="h2">{t(Resources.AnalysesListing.Title)}</Typography>
          <Typography variant="body1" color="secondary">
            {t(Resources.AnalysesListing.Subtitle)}
          </Typography>
        </Stack>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex">
            <TabFilter value={listingFilter} onChange={handleFilterChange}>
              <TabFilter.Item label={t(Resources.AnalysesListing.Status.All)} value={null} />
              <TabFilter.Item label={t(Resources.AnalysesListing.Status.InProgress)} value={AnalysisOverviewItemState.in_progress} />
              <TabFilter.Item label={t(Resources.AnalysesListing.Status.Completed)} value={AnalysisOverviewItemState.completed} />
              <TabFilter.Item
                label={t(Resources.AnalysesListing.Status.Declined)}
                value={AnalysisOverviewItemState.declined}
                $last={!isAnalysisSharingEnabled}
              />
              {isAnalysisSharingEnabled && (
                <TabFilter.Item label={t(Resources.AnalysesListing.Status.Shared)} value={SHARED_FILTER} $last />
              )}
            </TabFilter>
            <DateRangePicker
              title={t(Resources.Common.Created)}
              value={dateRange}
              onChange={(range) => {
                setDateRange(range);
              }}
            />
          </Box>
          <TextInput
            inputStartAdornment={<SearchIcon />}
            placeholder={t(Resources.Common.Search)}
            value={searchValue}
            sx={{ width: 320 }}
            onInputChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
        </Box>

        {isLoading && <Skeleton variant="rounded" height={500} />}
        {data && renderPageContent(data)}
      </Stack>
    </AuthenticatedLayout>
  );
};
